// Renders a user's avatar based on the string id stored on their profile.
// See lib/avatars_catalog.js for the id format.
//
// Usage:
//   <AvatarRender avatar={profile.avatar} name={profile.name} size={64} />
import React from "react";
import { parseAvatar } from "../lib/avatars_catalog";
import { getInitials, colorFromName } from "../lib/avatar";

export default function AvatarRender({ avatar, name = "", size = 64, radius = "50%" }) {
  const parsed = parseAvatar(avatar);

  // Default — initials on a colored gradient, same as before.
  if (parsed.kind === "default") {
    const color = colorFromName(name);
    return (
      <div
        data-testid={`avatar-render-default`}
        className="flex items-center justify-center font-display font-black"
        style={{
          width: size, height: size, borderRadius: radius,
          background: `linear-gradient(155deg, ${color}, #111)`,
          color: "#fff", fontSize: size * 0.35,
          border: `2px solid ${color}`, userSelect: "none",
        }}
      >
        {getInitials(name)}
      </div>
    );
  }

  // Suit — the playing-card suit glyph, big and on brand.
  if (parsed.kind === "suit") {
    return (
      <div
        data-testid={`avatar-render-suit-${parsed.suit}`}
        className="flex items-center justify-center font-display font-black"
        style={{
          width: size, height: size, borderRadius: radius,
          background: `radial-gradient(circle at 30% 30%, ${parsed.color}33, #111)`,
          color: parsed.color, fontSize: size * 0.62, lineHeight: 1,
          border: `2px solid ${parsed.color}`, userSelect: "none",
        }}
      >
        {parsed.glyph}
      </div>
    );
  }

  // Emoji — centred large, on a neutral dark background.
  if (parsed.kind === "emoji") {
    return (
      <div
        data-testid={`avatar-render-emoji`}
        className="flex items-center justify-center"
        style={{
          width: size, height: size, borderRadius: radius,
          background: "radial-gradient(circle at 30% 30%, #2A2E36, #0B0B0B)",
          fontSize: size * 0.6, lineHeight: 1,
          border: "2px solid rgba(255,255,255,0.12)", userSelect: "none",
        }}
      >
        {parsed.emoji}
      </div>
    );
  }

  // DiceBear — fetched SVG from CDN. Cached by the browser via Cache-Control.
  if (parsed.kind === "dice") {
    return (
      <img
        src={parsed.url}
        alt={`${name} avatar`}
        draggable={false}
        data-testid={`avatar-render-dice-${parsed.style}`}
        loading="lazy"
        style={{
          width: size, height: size, borderRadius: radius, display: "block",
          background: "#0B0B0B", border: "2px solid rgba(255,255,255,0.12)",
          userSelect: "none",
        }}
      />
    );
  }

  return null;
}
